import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { client } from "@/utils/orpc";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Card, CardContent } from "@super-resume-new/ui/components/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@super-resume-new/ui/components/table";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/admin/staging")({
  head: () =>
    seo({
      title: "Staging Buffer Queue | JobCues Admin",
      description: "Review and process scraped raw job postings awaiting AI information extraction.",
      path: "/admin/staging",
      noindex: true,
    }),
  component: AdminStagingPage,
});

function AdminStagingPage() {
  const [statusFilter, setStatusFilter] = useState<any>("all");
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const queryClient = useQueryClient();

  const {
    data: stagingData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["admin", "staging", page, statusFilter],
    queryFn: () =>
      client.admin.listStagingQueue({
        page,
        limit: pageSize,
        status: statusFilter,
      }),
  });

  // Extract single staged item
  const extractMutation = useMutation({
    mutationFn: async (id: string) => {
      return client.admin.extractStagedJob({ id });
    },
    onSuccess: (data) => {
      toast.success(`Extracted "${data.title}" -> ${data.status}`);
      refetch();
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Extraction failed");
    },
  });

  // Delete staged item
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return client.admin.deleteStagedJob({ id });
    },
    onSuccess: () => {
      toast.success("Staged item removed");
      refetch();
    },
    onError: (err: any) => toast.error(err.message || "Failed to delete"),
  });

  // Clear completed & duplicates
  const clearCompletedMutation = useMutation({
    mutationFn: async () => {
      await client.admin.clearStagingByStatus({ status: "completed" });
      await client.admin.clearStagingByStatus({ status: "skipped_duplicate" });
    },
    onSuccess: () => {
      toast.success("Cleaned up completed and duplicate staging rows");
      refetch();
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
  });

  // Reset failed
  const resetFailedMutation = useMutation({
    mutationFn: async () => {
      return client.admin.resetFailedStaged();
    },
    onSuccess: () => {
      toast.success("Reset all failed jobs to pending status");
      refetch();
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
  });

  const total = stagingData?.total ?? 0;
  const totalPages = stagingData?.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-border bg-card px-2.5 text-xs text-foreground font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
          >
            <option value="all">All Staging Statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="failed">Failed</option>
            <option value="completed">Completed</option>
            <option value="skipped_duplicate">Skipped (Duplicate)</option>
          </select>

          <Button
            size="sm"
            variant="outline"
            onClick={() => resetFailedMutation.mutate()}
            disabled={resetFailedMutation.isPending}
            className="text-xs"
          >
            Reset Failed
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => clearCompletedMutation.mutate()}
            disabled={clearCompletedMutation.isPending}
            className="text-xs"
          >
            Clear Cleaned
          </Button>
        </div>

        <div className="text-xs text-muted-foreground tabular-nums">
          {total} staged items in buffer
        </div>
      </div>

      <Card className="border-border bg-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job Title &amp; Company</TableHead>
                <TableHead>Source / Tier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Staged Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                    <Loader2 className="mx-auto size-6 animate-spin text-brand mb-2" />
                    Loading staging queue…
                  </TableCell>
                </TableRow>
              ) : stagingData?.items?.length ? (
                stagingData.items.map((row: any) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-semibold text-foreground">{row.title}</span>
                        <span className="text-xs text-muted-foreground">{row.company}</span>
                        {row.error && (
                          <span className="mt-1 text-[11px] text-destructive font-mono truncate max-w-sm">
                            Error: {row.error}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className="text-[11px] uppercase">
                          {row.source}
                        </Badge>
                        <span className="text-[11px] text-muted-foreground">T{row.tier}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          row.status === "completed"
                            ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30"
                            : row.status === "pending"
                              ? "bg-muted/10 text-muted-foreground border-border"
                              : row.status === "processing"
                                ? "bg-amber-500/10 text-amber-500 border-amber-500/30"
                                : row.status === "failed"
                                  ? "bg-destructive/10 text-destructive border-destructive/30"
                                  : "bg-muted text-muted-foreground"
                        }
                      >
                        {row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {row.status !== "completed" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => extractMutation.mutate(row.id)}
                            disabled={extractMutation.isPending}
                            className="h-8 text-xs gap-1 font-semibold text-brand-foreground"
                          >
                            <Sparkles className="size-3" /> Extract
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteMutation.mutate(row.id)}
                          className="size-8 p-0 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-muted-foreground text-sm">
                    No staging queue items match this filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="gap-1"
          >
            <ChevronLeft className="size-3.5" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="gap-1"
          >
            Next <ChevronRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
